import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React, { useEffect, useMemo, useState } from 'react';
import { cn } from '@ais-ui/utils';
import { useAISMapContext } from '../Map/AISMapContext';
import { PiArrowsClockwiseFill } from 'react-icons/pi';
import { PortDetailsPanel, PortIdentification } from './PortDetailsPanel';
import { PortActions } from './PortActions';
import { useDrawPort } from './useDrawPort';
import { usePermissions } from '../Permissions/PermissionsProvider';
import { MissingPermission } from '../../components/MissingPermission';
import { CENTERCOMPONENTS } from '../Map/MapPanel/CenterComponents';
import { useMapPanelContext } from '../Map/MapPanel/MapPanelContext';
import { useRoutes } from '../Routes/RoutesContext';
import { useSelectionState } from '../../components/SelectionStateProvider';

interface TargetDetailsMapProps {
  className?: string;
}

interface PortCardStackProps {
  className?: string;
}

const PortCardStack = ({ className }: PortCardStackProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { selectedPorts, setSelectedPorts } = useSelectionState();
  const goToNextCard = () => {
    if (currentIndex < selectedPorts.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
  };
  const { centerComponent, setCenterComponent } = useMapPanelContext();
  const { setPort } = useRoutes();

  const permissions = usePermissions();

  const multiplePortsSelected = useMemo(() => {
    return selectedPorts.length > 1;
  }, [selectedPorts]);

  const nextPortName = useMemo(() => {
    if (!multiplePortsSelected) {
      return '';
    }
    if (currentIndex < selectedPorts.length - 1) {
      const nextSelectedPort = selectedPorts[currentIndex + 1];
      return nextSelectedPort.name;
    } else {
      const nextSelectedPort = selectedPorts[0];
      return nextSelectedPort.name;
    }
  }, [currentIndex, multiplePortsSelected, selectedPorts]);

  const currentPort = useMemo(() => {
    if (selectedPorts.length > 0 && currentIndex < selectedPorts.length) {
      return selectedPorts[currentIndex];
    }
    return selectedPorts[0] || null;
  }, [currentIndex, selectedPorts]);

  useEffect(() => {
    if (selectedPorts.length > 0 && currentIndex >= selectedPorts.length) {
      setCurrentIndex(0);
    }
  }, [selectedPorts.length, currentIndex]);

  useDrawPort(currentPort);

  useEffect(() => {
    if (!permissions.port) {
      return;
    }

    setPort(currentPort);
    setCenterComponent(CENTERCOMPONENTS.ROUTES);

    return () => {
      setCenterComponent(CENTERCOMPONENTS.NONE);
    };
  }, [currentPort, permissions.port, setCenterComponent, setPort]);

  if (!currentPort) {
    return null;
  }

  return (
    <div
      className={cn(
        'relative max-h-screen',
        className,
        selectedPorts.length === 2 && 'ml-3',
        selectedPorts.length >= 2 && 'ml-6'
      )}
    >
      <Card
        id="portinfo-card"
        className={cn(
          'relative flex flex-col min-w-[464px] max-w-[464px] h-screen z-10 shadow-lg'
        )}
        style={{ maxHeight: 'calc(100vh - 2rem)' }}
      >
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg font-medium text-muted-semibold">
              {currentPort.name}
            </CardTitle>
            <div className="flex space-x-1 items-center">
              {multiplePortsSelected && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={'ghost'}
                      size={'icon'}
                      onClick={goToNextCard}
                    >
                      <PiArrowsClockwiseFill className="h-5 w-5"></PiArrowsClockwiseFill>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {`Go to next port: ${nextPortName}`}
                  </TooltipContent>
                </Tooltip>
              )}
              <Button
                onClick={() => setSelectedPorts([])}
                variant="ghost"
                size="icon"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                  className="h-4 w-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex flex-col overflow-hidden space-y-6 h-full">
          <div className="grid gap-4">
            <PortIdentification port={currentPort}></PortIdentification>
          </div>
          {permissions.nextport && <PortDetailsPanel port={currentPort} />}
          {!permissions.nextport && (
            <MissingPermission
              entity={'nextport'}
              srcImage={'empty_list_bro.png'}
            />
          )}
        </CardContent>
        <CardFooter className="min-h-52">
          <PortActions port={currentPort}></PortActions>
        </CardFooter>
      </Card>
      <div className="absolute top-0 left-0">
        {selectedPorts.length >= 3 && (
          <Card
            id="stack-card-2"
            className={cn(
              'absolute z-1 min-w-[464px] max-w-[464px] h-screen -top-3 -left-6 shadow-md cursor-default pointer-events-auto'
            )}
            style={{ maxHeight: 'calc(100vh - 2rem)' }}
          ></Card>
        )}
        {selectedPorts.length >= 2 && (
          <Card
            id="stack-card-1"
            className={cn(
              'absolute z-2 min-w-[464px] max-w-[464px] h-screen -top-1.5 -left-3 shadow-md cursor-default pointer-events-auto'
            )}
            style={{ maxHeight: 'calc(100vh - 2rem)' }}
          ></Card>
        )}
      </div>
    </div>
  );
};

export function PortDetails({ className }: TargetDetailsMapProps) {
  const { selectedPorts, setSelectedPorts } = useSelectionState();
  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedPorts([]);
      }
    };

    window.addEventListener('keydown', onEscape);

    return () => {
      window.removeEventListener('keydown', onEscape);
    };
  }, []);

  if (selectedPorts.length === 0) {
    return null;
  }

  return <PortCardStack className={className} />;
}
